import { useState, useEffect, useRef, useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  START_VENDOR_INVOICE_SEND,
  MY_VENDOR_INVOICE_SEND_JOBS,
  DISMISS_VENDOR_INVOICE_SEND_JOB,
} from 'graphql/invoices.gql';
import type { VendorInvoice, VendorInvoiceSendJob, VendorInvoiceSendJobRow } from '@custom-types/invoices';

// How often a running send's progress is re-read from the server.
export const VENDOR_INVOICE_SEND_POLL_MS = 5000;

export const isActiveSendJob = (job: VendorInvoiceSendJob) => job.status === 'queued' || job.status === 'processing';

// A Counterparty (CIB) vendor has one row per Primary vendor it belongs to, all sharing one
// vendor_id — the Primary vendor name tells those rows apart.
export const invoiceRowKey = (ippId: number, primaryVendorName?: string | null) => `${ippId}|${primaryVendorName ?? ''}`;
export const vendorPrimaryName = (v: VendorInvoice) => (v.is_counterparty_invoice && v.primary_vendor_name ? v.primary_vendor_name : null);

const isEmailed = (row: VendorInvoiceSendJobRow) => row.status === 'SENT' || row.status === 'SENT_NOT_RECORDED';

export type VendorInvoiceSendSummary = {
  type: 'success' | 'warning' | 'error';
  message: string;
  emailedCount: number;
};

// The result text for a finished send. `vendors` (the dashboard rows, when the send's month is the
// one shown) supply display names; otherwise the names the server reported are used.
// `keptSelected`: the not-sent vendors were left selected on this page for a quick retry.
export const summarizeVendorInvoiceSend = (
  job: VendorInvoiceSendJob,
  vendors: VendorInvoice[],
  { keptSelected = false }: { keptSelected?: boolean } = {},
): VendorInvoiceSendSummary => {
  const rows = job.rows;
  const nameOf = (row: VendorInvoiceSendJobRow) => {
    const dashboardRow = vendors.find((v) => v.vendor_id === row.ippId && vendorPrimaryName(v) === (row.primaryVendorName ?? null));
    const name = dashboardRow?.vendor_name ?? row.vendorName ?? `Vendor ${row.ippId}`;
    return row.primaryVendorName ? `${name} (CIB for ${row.primaryVendorName})` : name;
  };
  const describe = (row: VendorInvoiceSendJobRow) => (row.error ? `${nameOf(row)} (${row.error})` : nameOf(row));

  const failedRows = rows.filter((row) => row.status === 'FAILED');
  const notRecordedRows = rows.filter((row) => row.status === 'SENT_NOT_RECORDED');
  const unprocessedRows = rows.filter((row) => row.status === 'NOT_PROCESSED' || row.status === 'PENDING');
  const emailedCount = rows.filter(isEmailed).length;

  if (!failedRows.length && !notRecordedRows.length && !unprocessedRows.length) {
    return { type: 'success', message: `${rows.length} vendor invoice(s) sent successfully.`, emailedCount };
  }

  const problems: string[] = [];
  if (failedRows.length) {
    const retry = keptSelected
      ? 'they are still selected; click Create Invoices to send just these again.'
      : 'select them and click Create Invoices to send just these again.';
    problems.push(`Email failed for: ${failedRows.map(describe).join(', ')}. These were not marked as Sent and remain Approved — ${retry}`);
  }
  if (notRecordedRows.length) {
    problems.push(`Emailed but not marked as Sent: ${notRecordedRows.map(describe).join(', ')}. The vendor already has these.`);
  }
  if (unprocessedRows.length) {
    const retry = keptSelected
      ? 'They are still selected; click Create Invoices to try again.'
      : 'Select them and click Create Invoices to try again.';
    problems.push(`${unprocessedRows.length} vendor invoice(s) were not processed: ${job.errorMessage ?? 'The send stopped early.'} ${retry}`);
  }
  return {
    type: failedRows.length || unprocessedRows.length ? 'error' : 'warning',
    message: `${emailedCount} of ${rows.length} vendor invoice(s) emailed. ${problems.join(' ')}`,
    emailedCount,
  };
};

// The current user's background vendor invoice sends whose banner hasn't been dismissed. Read from
// the server (not the browser), so they survive a refresh, a closed tab and logging in again.
export const useVendorInvoiceSendJobs = () => {
  const apolloClient = useApolloClient();
  const [jobs, setJobs] = useState<VendorInvoiceSendJob[]>([]);
  const [startMutation] = useMutation(START_VENDOR_INVOICE_SEND);
  const [dismissMutation] = useMutation(DISMISS_VENDOR_INVOICE_SEND_JOB);
  const mounted = useRef(true);

  const refreshSendJobs = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({ query: MY_VENDOR_INVOICE_SEND_JOBS, fetchPolicy: 'network-only' });
      if (mounted.current) setJobs(data?.myVendorInvoiceSendJobs ?? []);
    } catch (err) {
      console.error('Failed to load vendor invoice sends:', err);
    }
  }, [apolloClient]);

  useEffect(() => {
    mounted.current = true;
    refreshSendJobs();
    return () => {
      mounted.current = false;
    };
  }, [refreshSendJobs]);

  // Poll only while a send is running.
  const hasActiveJob = jobs.some(isActiveSendJob);
  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const intervalId = setInterval(refreshSendJobs, VENDOR_INVOICE_SEND_POLL_MS);
    return () => clearInterval(intervalId);
  }, [hasActiveJob, refreshSendJobs]);

  // Queues the send; throws if the server refuses it (e.g. some invoices are already being sent).
  const startSendJob = async (updates: Record<string, unknown>[], sendRunId: string): Promise<VendorInvoiceSendJob | null> => {
    const { data } = await startMutation({ variables: { updates, sendRunId } });
    const job: VendorInvoiceSendJob | undefined = data?.startVendorInvoiceSend;
    if (!job) return null;
    setJobs((prev) => [job, ...prev.filter((j) => j.jobId !== job.jobId)]);
    return job;
  };

  const dismissSendJob = async (jobId: number) => {
    await dismissMutation({ variables: { jobId } });
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  };

  return { sendJobs: jobs, startSendJob, dismissSendJob, refreshSendJobs };
};
